/**
 * Générateur — Additionner, soustraire, multiplier et diviser pour résoudre
 * des problèmes et contrôler la vraisemblance de son résultat.
 * Attendu ID : 691 | Niveau : 5ème | Domaine : Opérations (cycle 4 rénové, cluster 1)
 *
 * Plan de cartes (validé par le responsable pédagogique) :
 *
 *   Carte « voyages » — Ex.5 Les livres du CDI
 *     Formats : texte_trou_saisie, texte_trou_drag, qcm_unique (rotation
 *     voyages / dernier voyage).
 *     Construction : b ∈ [9..25], q ∈ [12..40], r ∈ [2..b−1] (r ≥ 2 : dernier
 *     voyage incomplet garanti ET pluriel « {{r}} livres » toujours correct),
 *     a = b×q + r. Quotient, reste, voyages (q+1) et charge du dernier voyage
 *     (r) uniques par construction.
 *     Misconceptions : q (tronque), q+2 (double comptage), b−r (complément),
 *     b (croit le dernier voyage plein).
 *
 *   Carte « deux_etapes » — Ex.20 Le lecteur DVD
 *     Formats : texte_trou_saisie, qcm_unique, texte (saisie).
 *     Construction : tout en CENTIMES (entiers, zéro flottant). L = prix
 *     appareil, n ∈ {2,3,4} articles à p centimes, T = L + n×p. Les deux
 *     étapes (T−L puis ÷n) tombent juste par construction. Affichage via
 *     fmtC (virgule FR, zéros finaux retirés — convention des gens 6e).
 *     Misconceptions : T÷n (oublie l'appareil), T−L (s'arrête à l'étape 1),
 *     L÷n (mauvaise donnée).
 *
 *   Carte « haies » — Ex.13 Le 110 m haies
 *     Formats : texte_trou_saisie, qcm_unique (rotation distance finale /
 *     donnée inutile — conforme à la Remarque du doc cluster).
 *     Construction : course fixe 110 m (réalisme athlétisme), n = 10 haies,
 *     tout en CENTIMÈTRES : d1 ∈ [1250..1450], e ∈ [850..950] →
 *     position = d1 + 9e ∈ [8900..10000] → arrivée = 11000 − pos ∈
 *     [1000..2100] > 0 par bornes. La donnée inutile TOURNE (hauteur des
 *     haies / nombre de couloirs / dossard) — exactement une par énoncé.
 *     Misconceptions : une seule haie comptée, 10 espacements au lieu de 9,
 *     position confondue avec distance restante.
 *
 *   Carte « vraisemblance » — Ex.23 L'achat de Kamel
 *     Formats : qcm_unique (verdict + justification), tableau_vf.
 *     Construction : centimes ; prix frôlant les entiers (delta ∈ ±[1..5] c).
 *     Deux régimes : « clair » (somme des entiers ≠ B, verdict par ordre de
 *     grandeur) et « limite » (somme des entiers = B, deltas TOUS de même
 *     signe → verdict décidé par « un peu plus / un peu moins »). Le V/F
 *     inclut une paire d'affirmations de vérités opposées par construction
 *     (jamais tout-vrai / tout-faux).
 *
 *   Carte « perimetre » (hors quota) — Ex.3 Un peu de géométrie
 *     Formats : texte (saisie), qcm_unique. Figure SVG à l'échelle
 *     (elements_additionnels image) : triangle ABC, côtés AB et CB étiquetés,
 *     « ? » sur AC. AC = P − AB − CB unique par construction ; inégalité
 *     triangulaire garantie par tirage borné + repli déterministe (l'exercice
 *     source : AB=4, CB=6, AC=7, P=17).
 *     Misconceptions : P−AB (oublie un côté), AB+CB (somme au lieu de
 *     différence), P−CB.
 *
 * Adaptations d'énoncés (doc cluster → en ligne) :
 *   - Ex.5 : les questions a/b deviennent un texte à trous progressif
 *     (« 390 = 14 × .. + .. ; .. voyages ; .. livres ») et un QCM en rotation.
 *   - Ex.13 : la donnée inutile (hauteur) devient une question QCM fermée.
 *   - Ex.23 : « dis si… » devient QCM verdict+justification et tableau V/F.
 *   - Ex.3 : figure support en SVG, seule la zone de réponse est fermée.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
    filtrerDistracteurs,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 691;

const L_VOY   = 'Résoudre un problème de voyages (division euclidienne)';
const L_2ET   = "Résoudre un problème à deux étapes (prix d'un article)";
const L_HAIES = 'Résoudre un problème multi-étapes (course de haies)';
const L_VRAI  = "Contrôler la vraisemblance d'un résultat";
const L_PERIM = 'Trouver un côté connaissant le périmètre (figure)';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

/** Centimes (entier) → écriture décimale FR sans zéros finaux ("2490" → "24,9"). */
const fmtC = (centimes) => {
    const euros = Math.floor(centimes / 100);
    const cents = centimes % 100;
    if (cents === 0) return String(euros);
    const dec = String(cents).padStart(2, '0').replace(/0+$/, '');
    return `${euros},${dec}`;
};

/** Élément image d'énoncé — convention §12 : imageUrl en data-URI + maxWidth. */
const imageElement = (svg) => ({
    version: 1,
    elements: [{
        type: 'image',
        config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' },
    }],
});

/**
 * Déduplique des choix de QCM par valeur normalisée (virgule ≡ point).
 * Les candidats (misconceptions) sont MÉLANGÉS avant sélection → rotation des
 * familles de pièges (§0.3) ; les compléments restent un repli ordonné.
 */
const dedupeChoix = (bonne, candidats, complements) => {
    const norm = (s) => String(s).replace(',', '.').trim();
    const vus = new Set([norm(bonne)]);
    const retenus = [];
    [...shuffle(candidats), ...complements].forEach(c => {
        const n = norm(c);
        if (!vus.has(n) && retenus.length < 3) { vus.add(n); retenus.push(String(c)); }
    });
    return retenus;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Voyages (division euclidienne)
// ═══════════════════════════════════════════════════════════

const CTX_VOYAGES = [
    { qui: 'La documentaliste du CDI', pronom: 'Elle', objets: 'livres', mission: 'les ranger sur des étagères' },
    { qui: 'Le cuisinier de la cantine', pronom: 'Il', objets: 'bouteilles de jus', mission: 'les stocker dans la réserve' },
    { qui: 'La gardienne du gymnase', pronom: 'Elle', objets: 'chaises', mission: 'les installer dans la salle' },
    { qui: 'Le fleuriste du quartier', pronom: 'Il', objets: 'pots de fleurs', mission: 'les disposer en vitrine' },
];

/** Tire la situation de division euclidienne (a = b×q + r, 2 ≤ r < b).
 *  r ≥ 2 : le dernier voyage incomplet existe ET « {{r}} livres » reste au
 *  pluriel sur tous les tirages (défaut juge : « 1 livres » quand r = 1). */
const tirerVoyages = () => {
    const b = randInt(9, 25);
    const q = randInt(12, 40);
    const r = randInt(2, b - 1);
    const a = b * q + r;
    const ctx = randomChoice(CTX_VOYAGES);
    return { a, b, q, r, ctx };
};

const enonceVoyages = ({ a, b, ctx }) =>
    `<p>${ctx.qui} reçoit <strong>${a} ${ctx.objets}</strong> et doit ${ctx.mission}. ` +
    `${ctx.pronom} ne peut en transporter que <strong>${b}</strong> à la fois.</p>`;

const phraseVoyages = ({ a, b, q, r, ctx }) =>
    `${a} = ${b} × {{${q}}} + {{${r}}}\n` +
    `${ctx.pronom} devra faire {{${q + 1}}} voyages.\n` +
    `Au dernier voyage, ${ctx.pronom.toLowerCase()} transportera {{${r}}} ${ctx.objets}.`;

const genVoyagesTrouSaisie = () => {
    const t = tirerVoyages();
    const texte = enonceVoyages(t) + '<p>Complète le raisonnement :</p>';
    return makeTexteTrou(phraseVoyages(t), texte, { ...opts(L_VOY), mode: 'saisie' });
};

const genVoyagesTrouDrag = () => {
    const t = tirerVoyages();
    const phrase = phraseVoyages(t);
    // Misconceptions : q−1 / q+2 (mauvais compte de voyages), b−r (complément
    // du reste), b (croit le dernier voyage plein), r+2 (reste décalé),
    // q+3 (quotient plausible). 6 candidats mélangés → ≥ 3 survivent au filtre
    // sur tout tirage (au plus 3 collisions simultanées possibles) + rotation.
    const candidats = shuffle([t.q - 1, t.q + 2, t.b - t.r, t.b, t.r + 2, t.q + 3].map(String));
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    const texte = enonceVoyages(t) + '<p>Complète le raisonnement avec les bonnes étiquettes :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_VOY), mode: 'drag', distracteurs });
};

const genVoyagesQcm = () => {
    const t = tirerVoyages();
    const { b, q, r, ctx } = t;
    if (randomChoice([true, false])) {
        // Question a — nombre minimal de voyages (bonne réponse : q+1)
        const bonne = String(q + 1);
        const distracteurs = dedupeChoix(bonne, [q, q + 2, r].map(String), [q + 3, q - 1].map(String));
        const texte = enonceVoyages(t) +
            `<p>Combien de voyages devra-t-${ctx.pronom === 'Elle' ? 'elle' : 'il'} faire au minimum ?</p>`;
        return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_VOY));
    }
    // Question b — objets transportés au dernier voyage (bonne réponse : r)
    const bonne = String(r);
    const distracteurs = dedupeChoix(bonne, [b - r, b, q].map(String), [r + 1, r + 3].map(String));
    const texte = enonceVoyages(t) +
        `<p>Combien de ${ctx.objets} transportera-t-${ctx.pronom === 'Elle' ? 'elle' : 'il'} au dernier voyage ?</p>`;
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_VOY));
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Problème à deux étapes (appareil + n articles)
// ═══════════════════════════════════════════════════════════

const PRENOMS = ['Alice', 'Kamel', 'Inès', 'Lucas', 'Maya', 'Nolan', 'Sofia', 'Rayan'];

const CTX_2ETAPES = [
    { appareil: 'un lecteur DVD', def: 'Le lecteur DVD', article: 'DVD', articles: 'DVD', unArticle: 'un DVD', chaque: 'chaque DVD' },
    { appareil: 'une console de jeux', def: 'La console', article: 'jeu vidéo', articles: 'jeux vidéo', unArticle: 'un jeu vidéo', chaque: 'chaque jeu vidéo' },
    { appareil: 'une imprimante', def: "L'imprimante", article: "cartouche d'encre", articles: "cartouches d'encre", unArticle: "une cartouche d'encre", chaque: "chaque cartouche d'encre" },
    { appareil: 'une cafetière', def: 'La cafetière', article: 'paquet de café', articles: 'paquets de café', unArticle: 'un paquet de café', chaque: 'chaque paquet de café' },
];

/** Tout en centimes. Centimes sans zéro final pour éviter « 12,50 » vs « 12,5 ». */
const CENTS_PROPRES = [15, 25, 35, 45, 55, 65, 75, 85, 95];

const tirer2Etapes = () => {
    const n = randInt(2, 4);
    const L = randInt(29, 79) * 100 + randomChoice(CENTS_PROPRES);   // appareil
    const p = randInt(8, 25) * 100 + randomChoice(CENTS_PROPRES);    // article
    const S = n * p;                                                  // total articles
    const T = L + S;                                                  // total payé
    return { n, L, p, S, T, prenom: randomChoice(PRENOMS), ctx: randomChoice(CTX_2ETAPES) };
};

const enonce2Etapes = ({ n, L, T, prenom, ctx }) =>
    `<p>${prenom} a acheté ${ctx.appareil} et <strong>${n} ${ctx.articles}</strong>. ` +
    `${ctx.def} coûte <strong>${fmtC(L)} €</strong> ` +
    `et ${ctx.chaque} coûte le même prix. ${prenom} a payé en tout <strong>${fmtC(T)} €</strong>.</p>`;

const gen2EtapesTrouSaisie = () => {
    const t = tirer2Etapes();
    const phrase =
        `L'ensemble des ${t.n} ${t.ctx.articles} coûte ${fmtC(t.T)} − ${fmtC(t.L)} = {{${fmtC(t.S)}}} €.\n` +
        `${t.ctx.unArticle.charAt(0).toUpperCase() + t.ctx.unArticle.slice(1)} coûte {{${fmtC(t.S)}}} ÷ ${t.n} = {{${fmtC(t.p)}}} €.`;
    const texte = enonce2Etapes(t) +
        '<p>Complète le raisonnement (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_2ET), mode: 'saisie' });
};

const gen2EtapesQcm = () => {
    const t = tirer2Etapes();
    const bonne = fmtC(t.p);
    // Misconceptions : T÷n (oublie de soustraire l'appareil, arrondi aux
    // centimes), T−L (s'arrête à l'étape 1), L÷n (divise la mauvaise donnée).
    const tSurN = Math.round(t.T / t.n);
    const lSurN = Math.round(t.L / t.n);
    const distracteurs = dedupeChoix(bonne,
        [fmtC(tSurN), fmtC(t.S), fmtC(lSurN)],
        [fmtC(t.p + 100), fmtC(t.p + 50)]);
    const texte = enonce2Etapes(t) + `<p>Quel est le prix d'${t.ctx.unArticle} ?</p>`;
    return makeQcm([`${bonne} €`, ...distracteurs.map(d => `${d} €`)], `${bonne} €`, texte, opts(L_2ET));
};

const gen2EtapesSaisie = () => {
    const t = tirer2Etapes();
    const texte = enonce2Etapes(t) +
        `<p>Quel est le prix d'${t.ctx.unArticle} ? Écris ta réponse en écriture décimale (ex. : 12,5).</p>`;
    return makeSaisie(fmtC(t.p), texte, { ...opts(L_2ET), suffixe: '€' });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Course de haies (multi-étapes, donnée inutile)
// ═══════════════════════════════════════════════════════════

/**
 * Donnée inutile injectée dans l'énoncé — EN ROTATION (défaut juge : « la
 * hauteur » systématique devenait un pattern mémorisable). Exactement UNE
 * donnée inutile par tirage → bonne réponse du QCM unique par construction.
 */
const DONNEES_INUTILES = [
    { fragment: (v) => `, chaque haie a une hauteur de ${v} m`, choix: (v) => `La hauteur des haies (${v} m)`, valeurs: ['0,838', '0,914', '1,067'] },
    { fragment: (v) => `, la piste compte ${v} couloirs`, choix: (v) => `Le nombre de couloirs (${v})`, valeurs: ['6', '8', '9'] },
    { fragment: (v) => `, l'athlète porte le dossard n° ${v}`, choix: (v) => `Le numéro de dossard (${v})`, valeurs: ['421', '87', '305'] },
];

/** Tout en centimètres : position = d1 + 9e ≤ 10000 < 11000 par bornes. */
const tirerHaies = () => {
    const d1 = randInt(1250, 1450);      // première haie : 12,50 m à 14,50 m
    const e = randInt(850, 950);         // espacement : 8,50 m à 9,50 m
    const pos = d1 + 9 * e;              // dernière haie (10 haies → 9 espacements)
    const arr = 11000 - pos;             // distance à l'arrivée, > 0 par bornes
    const inutile = randomChoice(DONNEES_INUTILES);
    return { d1, e, pos, arr, inutile, vInutile: randomChoice(inutile.valeurs) };
};

/** Centimètres → mètres FR sans zéros finaux ("1372" → "13,72", "8900" → "89"). */
const fmtM = (cm) => {
    const m = Math.floor(cm / 100);
    const c = cm % 100;
    if (c === 0) return String(m);
    const dec = String(c).padStart(2, '0').replace(/0+$/, '');
    return `${m},${dec}`;
};

const enonceHaies = ({ d1, e, inutile, vInutile }) =>
    `<p>Sur un <strong>110 m haies</strong> comportant dix haies${inutile.fragment(vInutile)}. ` +
    `La première haie est à <strong>${fmtM(d1)} m</strong> de la ligne de départ. ` +
    `Les haies sont espacées de <strong>${fmtM(e)} m</strong>.</p>`;

const genHaiesTrouSaisie = () => {
    const t = tirerHaies();
    const phrase =
        `La dernière haie est à ${fmtM(t.d1)} + 9 × ${fmtM(t.e)} = {{${fmtM(t.pos)}}} m du départ.\n` +
        `La distance de la dernière haie à l'arrivée est 110 − {{${fmtM(t.pos)}}} = {{${fmtM(t.arr)}}} m.`;
    const texte = enonceHaies(t) +
        '<p>Quelle est la distance de la dernière haie à la ligne d\'arrivée ? ' +
        'Complète (écris les nombres sans zéro inutile, ex. : 12,5) :</p>';
    return makeTexteTrou(phrase, texte, { ...opts(L_HAIES), mode: 'saisie' });
};

const genHaiesQcm = () => {
    const t = tirerHaies();
    if (randomChoice([true, false])) {
        // Question fermée sur la donnée inutile (Remarque du doc cluster).
        // La donnée inutile TOURNE (hauteur / couloirs / dossard) — exactement
        // une par énoncé → bonne réponse unique par construction.
        const bonne = t.inutile.choix(t.vInutile);
        const autres = [
            `L'espacement entre les haies (${fmtM(t.e)} m)`,
            `La position de la première haie (${fmtM(t.d1)} m)`,
            'La longueur de la course (110 m)',
        ];
        const texte = enonceHaies(t) +
            '<p>On cherche la distance de la dernière haie à la ligne d\'arrivée. ' +
            'Quelle donnée de l\'énoncé est <strong>inutile</strong> pour répondre ?</p>';
        return makeQcm([bonne, ...autres], bonne, texte, opts(L_HAIES));
    }
    // Question numérique — distance de la dernière haie à l'arrivée
    const bonne = `${fmtM(t.arr)} m`;
    // Misconceptions : une seule haie comptée, 10 espacements au lieu de 9,
    // position confondue avec la distance restante.
    const uneHaie = 11000 - (t.d1 + t.e);
    const dixEsp = 11000 - (t.d1 + 10 * t.e);
    const distracteurs = dedupeChoix(bonne,
        [`${fmtM(uneHaie)} m`, `${fmtM(dixEsp)} m`, `${fmtM(t.pos)} m`],
        [`${fmtM(t.arr + 100)} m`, `${fmtM(t.arr + 50)} m`]);
    const texte = enonceHaies(t) +
        '<p>Quelle est la distance de la dernière haie à la ligne d\'arrivée ?</p>';
    return makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_HAIES));
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Vraisemblance (achat sans poser d'opération)
// ═══════════════════════════════════════════════════════════

const CTX_VRAI = [
    { item: 'stylo', items: 'stylos', autre: 'un cahier', autreNom: 'le cahier' },
    { item: 'gomme', items: 'gommes', autre: 'un classeur', autreNom: 'le classeur' },
    { item: 'croissant', items: 'croissants', autre: 'une brioche', autreNom: 'la brioche' },
    { item: 'carnet', items: 'carnets', autre: 'une trousse', autreNom: 'la trousse' },
];

/**
 * Deux régimes par construction :
 *  - « limite » : n×basP + basC = B ; les deltas sont tirés TOUS du même signe
 *    → le verdict (dépasse / ne dépasse pas) découle du signe, décidable par
 *    « un peu plus / un peu moins » sans poser d'opération.
 *  - « clair »  : n×basP + basC = B ± 1 € au moins ; le verdict découle des
 *    ordres de grandeur, quels que soient les signes des deltas.
 */
const tirerVraisemblance = () => {
    const ctx = randomChoice(CTX_VRAI);
    const prenom = randomChoice(PRENOMS);
    const n = randInt(2, 4);
    const basP = randomChoice([1, 2]);          // € entiers frôlés par l'article
    if (randomChoice([true, false])) {
        // Régime « limite » : basC calé pour que n×basP + basC = B
        const B = randomChoice([5, 10]);
        const basC = B - n * basP;
        if (basC >= 1 && basC <= 4) {
            const depasse = randomChoice([true, false]);
            const signe = depasse ? 1 : -1;
            const p = basP * 100 + signe * randInt(1, 3);
            const c = basC * 100 + signe * randInt(1, 5);
            return { ctx, prenom, n, basP, basC, B, p, c, depasse, regime: 'limite' };
        }
        // basC hors bornes réalistes → repli sur le régime clair ci-dessous
    }
    // Régime « clair » : total entier ≥ B+1 (dépasse) ou ≤ B−1 (passe).
    // B = n×basP + basC ∓ 1 ≥ 2×1 + 2 − 1 = 3 par bornes : toujours valide.
    const depasse = randomChoice([true, false]);
    const basC = randInt(2, 4);
    const B = depasse ? n * basP + basC - 1 : n * basP + basC + 1;
    const p = basP * 100 + randomChoice([-2, -1, 1, 2]);
    const c = basC * 100 + randomChoice([-5, -2, -1, 1, 2, 5]);
    return { ctx, prenom, n, basP, basC, B, p, c, depasse, regime: 'clair' };
};

const enonceVraisemblance = ({ ctx, prenom, n, p, c, B }) =>
    `<p>${prenom} veut acheter <strong>${n} ${ctx.items}</strong> à ${fmtC(p)} € pièce ` +
    `et ${ctx.autre} à <strong>${fmtC(c)} €</strong>. ` +
    `${prenom} a <strong>${B} €</strong> en poche.</p>`;

const genVraisemblanceQcm = () => {
    const t = tirerVraisemblance();
    const { ctx, n, basP, basC, B, p, c, depasse } = t;
    const plusMoinsP = p > basP * 100 ? 'un peu plus' : 'un peu moins';
    const environC = c > basC * 100 ? `un peu plus de ${basC} €` : `presque ${basC} €`;
    const bonne = depasse
        ? `Non, car ${n} × ${fmtC(p)} € coûte ${plusMoinsP} de ${n * basP} € et ${ctx.autreNom} coûte ${environC} : le total dépasse ${B} €.`
        : `Oui, car ${n} × ${fmtC(p)} € coûte ${plusMoinsP} de ${n * basP} € et ${ctx.autreNom} coûte ${environC} : le total ne dépasse pas ${B} €.`;
    const mauvaises = [
        // Verdict inversé, « justifié » en arrondissant tout dans le mauvais sens
        depasse
            ? `Oui, car chaque ${ctx.item} coûte environ ${basP} € et ${ctx.autreNom} environ ${basC} €, donc il reste de la monnaie.`
            : `Non, car les prix ne sont pas des nombres entiers, donc le total dépasse forcément ${B} €.`,
        // Oublie de multiplier par n
        `${depasse ? 'Oui' : 'Non'}, car ${fmtC(p)} € + ${fmtC(c)} € ${depasse ? `ne dépasse pas ${B} €` : `dépasse déjà ${B} €`}.`,
        // Refuse de conclure sans calculatrice
        'On ne peut pas savoir sans poser l\'opération ou utiliser une calculatrice.',
    ];
    const texte = enonceVraisemblance(t) +
        `<p><strong>Sans poser d'opération</strong>, ${t.prenom} pourra-t-${['Alice', 'Inès', 'Maya', 'Sofia'].includes(t.prenom) ? 'elle' : 'il'} réaliser cet achat ? ` +
        'Choisis la réponse accompagnée de la justification correcte.</p>';
    return makeQcm([bonne, ...mauvaises], bonne, texte, opts(L_VRAI));
};

const genVraisemblanceVF = () => {
    const t = tirerVraisemblance();
    const { ctx, prenom, n, basP, basC, B, p, c, depasse } = t;
    // Vérités connues PAR CONSTRUCTION (aucun calcul de scoring à la volée) :
    const lignes = [
        `${n} ${ctx.items} coûtent un peu plus de ${n * basP} €`,
        `${ctx.autreNom.charAt(0).toUpperCase() + ctx.autreNom.slice(1)} coûte moins de ${basC} €`,
        `Le total des achats dépasse ${B} €`,
        `Avec ${B} €, ${prenom} peut payer tous ses achats`,
    ];
    const bonnes = [
        p > basP * 100 ? 0 : 1,   // « un peu plus de n×basP € » ⟺ p > basP
        c < basC * 100 ? 0 : 1,   // « coûte moins de basC € »  ⟺ c < basC
        depasse ? 0 : 1,          // verdict par construction
        depasse ? 1 : 0,          // négation de la ligne 3 → mélange V/F garanti
    ];
    const texte = enonceVraisemblance(t) +
        '<p><strong>Sans poser d\'opération</strong>, indique si chaque affirmation est vraie ou fausse.</p>';
    return makeVraiFaux(lignes, bonnes, texte, opts(L_VRAI));
};

// ═══════════════════════════════════════════════════════════
// Carte 5 (hors quota) — Côté manquant connaissant le périmètre
// ═══════════════════════════════════════════════════════════

/** Tirage borné + repli déterministe (exercice source : 4, 6, 7 → P = 17). */
const tirerTriangle = () => {
    for (let i = 0; i < 40; i++) {
        const ab = randInt(3, 9);
        const cb = randInt(4, 10);
        const ac = randInt(3, 9);
        const distincts = ab !== cb && ab !== ac && cb !== ac;
        const inegalite = ab + cb > ac && ab + ac > cb && cb + ac > ab;
        if (distincts && inegalite) return { ab, cb, ac, p: ab + cb + ac };
    }
    return { ab: 4, cb: 6, ac: 7, p: 17 };
};

/**
 * Figure SVG à l'échelle : BC horizontal, A au-dessus (intersection de cercles).
 * Marges généreuses gauche/droite pour les étiquettes de côtés — et décalage
 * par min(0, xA) : pour un triangle obtus en B, A part à gauche de B, la figure
 * est translatée pour rester entièrement dans le viewBox.
 */
const svgTriangle = ({ ab, cb, ac }) => {
    const s = 28;                                     // px par cm
    const d = cb * s;                                 // BC horizontal
    const x = (d * d + ab * ab * s * s - ac * ac * s * s) / (2 * d);
    const y = Math.sqrt(Math.max(ab * ab * s * s - x * x, 100));
    const margeG = 100, margeD = 100, margeV = 40;
    const offX = margeG + Math.max(0, -x);            // décale si A est à gauche de B
    const h = Math.round(y + 2 * margeV);
    const B = { x: offX, y: h - margeV };
    const C = { x: offX + d, y: h - margeV };
    const A = { x: offX + x, y: h - margeV - y };
    const w = Math.round(Math.max(B.x, C.x, A.x) + margeD);
    const mid = (P, Q) => ({ x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 });
    const mAB = mid(A, B), mAC = mid(A, C), mBC = mid(B, C);
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" font-family="sans-serif">` +
        `<polygon points="${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}" fill="none" stroke="#1E3A8A" stroke-width="2"/>` +
        `<text x="${A.x}" y="${A.y - 10}" text-anchor="middle" font-size="15" font-weight="bold" fill="#1E3A8A">A</text>` +
        `<text x="${B.x - 12}" y="${B.y + 16}" font-size="15" font-weight="bold" fill="#1E3A8A">B</text>` +
        `<text x="${C.x + 4}" y="${C.y + 16}" font-size="15" font-weight="bold" fill="#1E3A8A">C</text>` +
        `<text x="${mAB.x - 10}" y="${mAB.y - 6}" text-anchor="end" font-size="13" fill="#334155">AB = ${ab} cm</text>` +
        `<text x="${mAC.x + 10}" y="${mAC.y - 6}" font-size="13" fill="#DC2626" font-weight="bold">AC = ?</text>` +
        `<text x="${mBC.x}" y="${mBC.y + 18}" text-anchor="middle" font-size="13" fill="#334155">CB = ${cb} cm</text>` +
        '</svg>';
};

const enonceTriangle = ({ ab, cb, p }) =>
    `<p>Le triangle ABC ci-dessous a un périmètre de <strong>${p} cm</strong>. ` +
    `On donne AB = ${ab} cm et CB = ${cb} cm.</p>`;

const genPerimetreSaisie = () => {
    const t = tirerTriangle();
    const texte = enonceTriangle(t) + '<p>Quelle est la longueur du côté AC ?</p>';
    return makeSaisie(String(t.ac), texte, {
        ...opts(L_PERIM), suffixe: 'cm',
        elements_additionnels: imageElement(svgTriangle(t)),
    });
};

const genPerimetreQcm = () => {
    const t = tirerTriangle();
    const bonne = `${t.ac} cm`;
    // Misconceptions : P−AB (oublie un côté), AB+CB (additionne au lieu de
    // soustraire), P−CB (oublie l'autre côté).
    const distracteurs = dedupeChoix(bonne,
        [`${t.p - t.ab} cm`, `${t.ab + t.cb} cm`, `${t.p - t.cb} cm`],
        [`${t.ac + 1} cm`, `${t.ac + 2} cm`]);
    const texte = enonceTriangle(t) + '<p>Quelle est la longueur du côté AC ?</p>';
    const payload = makeQcm([bonne, ...distracteurs], bonne, texte, opts(L_PERIM));
    payload.elements_additionnels = imageElement(svgTriangle(t));
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    voyages_texte_trou_saisie:     genVoyagesTrouSaisie,
    voyages_texte_trou_drag:       genVoyagesTrouDrag,
    voyages_qcm_unique:            genVoyagesQcm,
    deux_etapes_texte_trou_saisie: gen2EtapesTrouSaisie,
    deux_etapes_qcm_unique:        gen2EtapesQcm,
    deux_etapes_texte:             gen2EtapesSaisie,
    haies_texte_trou_saisie:       genHaiesTrouSaisie,
    haies_qcm_unique:              genHaiesQcm,
    vraisemblance_qcm_unique:      genVraisemblanceQcm,
    vraisemblance_tableau_vf:      genVraisemblanceVF,
    perimetre_texte:               genPerimetreSaisie,
    perimetre_qcm_unique:          genPerimetreQcm,
};

export const CATEGORIES = [
    {
        id: 'voyages', label: L_VOY,
        formatsList: [
            { key: 'voyages_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'voyages_texte_trou_drag',   label: 'Drag' },
            { key: 'voyages_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'deux_etapes', label: L_2ET,
        formatsList: [
            { key: 'deux_etapes_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'deux_etapes_qcm_unique',        label: 'QCM' },
            { key: 'deux_etapes_texte',             label: 'Saisie' },
        ],
    },
    {
        id: 'haies', label: L_HAIES,
        formatsList: [
            { key: 'haies_texte_trou_saisie', label: 'Saisie trous' },
            { key: 'haies_qcm_unique',        label: 'QCM' },
        ],
    },
    {
        id: 'vraisemblance', label: L_VRAI,
        formatsList: [
            { key: 'vraisemblance_qcm_unique', label: 'QCM' },
            { key: 'vraisemblance_tableau_vf', label: 'Vrai/Faux' },
        ],
    },
    {
        id: 'perimetre', label: L_PERIM,
        formatsList: [
            { key: 'perimetre_texte',      label: 'Saisie' },
            { key: 'perimetre_qcm_unique', label: 'QCM' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    fmtC, fmtM, tirerVoyages, tirer2Etapes, tirerHaies,
    tirerVraisemblance, tirerTriangle, svgTriangle, dedupeChoix,
};
